import type React from 'react';
import { styled, css } from 'styled-components';

import Icons from '../../Elements/Icons/index.tsx';
import Loader from '../../Elements/Loader/index.tsx';
import { StyledCheckbox } from '../../Form/Checkbox/index.tsx';
import IconsV2 from '../../V2/Elements/Icons/index.ts';
import { Tooltip } from '../../V2/Feedback/Tooltip/index.tsx';
import type { TooltipType } from '../../V2/Feedback/Tooltip/types.ts';

const LoaderWrapper = styled.span`
  margin-left: 5px;
`;

export interface LabelProps extends Omit<React.HTMLProps<HTMLLabelElement>, 'as' | 'ref' | 'css'> {
  required?: boolean;
  tooltip?: React.ReactNode;
  inline?: boolean;
  isLoading?: boolean;
  type?: 'inline-checkbox' | 'inline-checkbox-inform';
  tooltipIcon?: 'question' | 'warning' | 'info';
  tooltipType?: TooltipType;
  isCheckbox?: boolean;
  margin?: string;
  nowrap?: boolean;
}

export const StyledLabel = styled.label<{ $styled: LabelProps }>`
  color: ${({ theme }) => theme.colors.grey100_1};
  font-size: 14px;
  line-height: 16px;
  font-weight: normal;
  align-items: center;
  display: ${({ $styled: { isCheckbox } }) => (isCheckbox ? 'inline-flex' : 'flex')};
  margin: ${({ $styled: { margin } }) => margin};
  ${({ $styled: { nowrap } }) =>
    nowrap &&
    css`
      white-space: nowrap;
    `}
  ${({ $styled: { type, margin } }) =>
    type === 'inline-checkbox-inform' &&
    css`
      margin: ${!margin && '26px 0 0 0'};
      height: 38px;
    `}
  ${({ $styled: { isCheckbox, margin } }) =>
    !isCheckbox &&
    css`
      margin: ${!margin && '0 0 10px'};
    `}
  ${({ $styled: { isCheckbox } }) =>
    isCheckbox &&
    css`
      cursor: pointer;
      *:first-child:not(:last-child) {
        margin-right: 5px;
      }
      &:hover {
        ${StyledCheckbox} {
          border-color: ${({ theme }) => theme.colors.grey70_1};
        }
      }
    `}
`;

export const RequiredMarker = styled.span`
  display: inline-block;
  color: ${({ theme }) => theme.colors.critical100};
  margin-left: 3px;
  width: 6px;

  &::before {
    content: '*';
  }
`;

const TooltipContainer = styled.span`
  display: inline-flex;
  margin: 0 0 0 3px;
`;

const Label: React.FC<LabelProps> = ({
  children,
  required = false,
  tooltip: $tooltip = null,
  inline = false,
  isLoading = false,
  type,
  tooltipIcon = 'question',
  tooltipType = 'tooltip',
  margin,
  nowrap,
  ...other
}) => (
  <StyledLabel
    $styled={{
      tooltipIcon,
      required,
      inline,
      isLoading,
      type,
      isCheckbox: type === 'inline-checkbox' || type === 'inline-checkbox-inform',
      margin,
      nowrap,
    }}
    {...other}>
    {children}
    {required && <RequiredMarker />}
    {isLoading && (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    )}
    {$tooltip && (
      <Tooltip type={tooltipType} placement='top' tooltip={$tooltip} ContainerComponent={TooltipContainer}>
        {tooltipIcon === 'question' && <Icons.QuestionCircle size={14} />}
        {tooltipIcon === 'warning' && <Icons.WarningCircle size={12} />}
        {tooltipIcon === 'info' && <IconsV2.Info filled size={16} />}
      </Tooltip>
    )}
  </StyledLabel>
);

export default Label;
